import {
  NewRequestItemInput,
  NewReturnEvidenceRequirement,
  NewReturnPolicy,
} from 'types/newRequest';

export interface NewRequestPolicySettings {
  policySettings?: Record<string, any> | null;
  resolvePortalOptions?: Record<string, any> | null;
}

export function getNewRequestReturnsEnabled(
  policySettings?: Record<string, any> | null,
  resolvePortalOptions?: Record<string, any> | null,
): boolean {
  const explicitValue = [
    resolvePortalOptions?.returnFilingEnabled,
    resolvePortalOptions?.returnsEnabled,
    resolvePortalOptions?.returns?.enabled,
    policySettings?.returns?.enabled,
  ].find((value) => typeof value === 'boolean');
  return typeof explicitValue === 'boolean' ? explicitValue : true;
}

export interface NewReturnReason {
  id: string;
  label: string;
  analyticsKey: string;
}

const presetReasons: NewReturnReason[] = [
  { id: 'WRONG_ITEM', label: 'Wrong item', analyticsKey: 'WRONG_ITEM' },
  { id: 'DAMAGED', label: 'Damaged or defective', analyticsKey: 'DAMAGED' },
  {
    id: 'NOT_AS_DESCRIBED',
    label: 'Not as described',
    analyticsKey: 'NOT_AS_DESCRIBED',
  },
  {
    id: 'MISSING_ITEM_OR_PARTS',
    label: 'Missing item or parts',
    analyticsKey: 'MISSING_ITEM_OR_PARTS',
  },
  { id: 'DOES_NOT_FIT', label: "Doesn't fit", analyticsKey: 'DOES_NOT_FIT' },
  { id: 'CHANGED_MIND', label: 'Changed mind', analyticsKey: 'CHANGED_MIND' },
  { id: 'ARRIVED_LATE', label: 'Arrived late', analyticsKey: 'ARRIVED_LATE' },
  { id: 'OTHER', label: 'Other', analyticsKey: 'OTHER' },
];
const defaultReasons = ['WRONG_ITEM', 'DAMAGED', 'CHANGED_MIND', 'OTHER'];
const normalize = (value: unknown) =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

function customReasons(settings: NewRequestPolicySettings): NewReturnReason[] {
  const policyReasons =
    settings.policySettings?.returnReasons ||
    settings.policySettings?.return_reasons ||
    {};
  const portalReasons = settings.resolvePortalOptions?.customReturnReasons;
  const values = Array.isArray(portalReasons)
    ? portalReasons
    : policyReasons.customReasons || policyReasons.custom_reasons;
  if (!Array.isArray(values)) return [];
  return values.flatMap((reason: any) => {
    if (!reason || typeof reason !== 'object') return [];
    const id = typeof reason.id === 'string' ? reason.id.trim() : '';
    const analyticsKey =
      typeof (reason.analyticsKey ?? reason.analytics_key) === 'string'
        ? String(reason.analyticsKey ?? reason.analytics_key).trim()
        : id;
    if (!id && !analyticsKey) return [];
    return [
      {
        id: id || analyticsKey,
        analyticsKey: analyticsKey || id,
        label: String(
          reason.label || reason.title || reason.name || id || analyticsKey,
        ).trim(),
      },
    ];
  });
}

function reasonAliases(
  reason: string,
  settings: NewRequestPolicySettings,
): string[] {
  const normalized = normalize(reason);
  const custom = customReasons(settings).filter((entry) =>
    [normalize(entry.id), normalize(entry.analyticsKey)].includes(normalized),
  );
  return Array.from(
    new Set([
      normalized,
      ...custom.flatMap((entry) => [
        normalize(entry.id),
        normalize(entry.analyticsKey),
      ]),
    ]),
  );
}

function isDefectReason(
  reason: string,
  settings: NewRequestPolicySettings,
): boolean {
  return reasonAliases(reason, settings).some((value) =>
    /(^|[^A-Z])(DAMAGED?|DEFECT(?:IVE|S)?|BROKEN)([^A-Z]|$)/.test(value),
  );
}

export function getNewReturnReasons(
  policy: NewReturnPolicy | null | undefined,
  settings: NewRequestPolicySettings = {},
): NewReturnReason[] {
  const allReasons = [...presetReasons, ...customReasons(settings)];
  const enabled = policy?.returnReasonPolicyConfigured
    ? policy.enabledReturnReasons || []
    : defaultReasons;
  return enabled.map((id) => {
    const reason = allReasons.find((entry) =>
      [normalize(entry.id), normalize(entry.analyticsKey)].includes(
        normalize(id),
      ),
    );
    return reason
      ? { ...reason, id }
      : {
          id,
          analyticsKey: id,
          label: id
            .replace(/^custom_/i, '')
            .replace(/_/g, ' ')
            .toLowerCase(),
        };
  });
}

export function isDefectReturn(
  items: Array<Pick<NewRequestItemInput, 'reason'>>,
  settings: NewRequestPolicySettings = {},
): boolean {
  return items.some((item) => isDefectReason(item.reason, settings));
}

export function getNewReturnEvidence(
  policy: NewReturnPolicy | null | undefined,
  reason: string,
  settings: NewRequestPolicySettings = {},
): {
  notes: NewReturnEvidenceRequirement | 'REASON_BASED';
  photos: NewReturnEvidenceRequirement | 'REASON_BASED';
  defect: boolean;
} {
  const aliases = reasonAliases(reason, settings);
  const configured = Object.entries(
    policy?.evidenceRequirements?.byReason || {},
  ).find(([key]) => aliases.includes(normalize(key)))?.[1];
  if (isDefectReason(reason, settings)) {
    const explicit = (value: unknown): NewReturnEvidenceRequirement =>
      ['HIDDEN', 'OPTIONAL', 'REQUIRED'].includes(String(value))
        ? (value as NewReturnEvidenceRequirement)
        : 'REQUIRED';
    return {
      notes: explicit(
        configured?.notes ?? policy?.evidenceRequirements?.defaults?.notes,
      ),
      photos: explicit(
        configured?.photos ?? policy?.evidenceRequirements?.defaults?.photos,
      ),
      defect: true,
    };
  }
  return {
    notes:
      configured?.notes ??
      policy?.evidenceRequirements?.defaults?.notes ??
      'REASON_BASED',
    photos:
      configured?.photos ??
      policy?.evidenceRequirements?.defaults?.photos ??
      'REASON_BASED',
    defect: false,
  };
}

export function isCompletedReturnPhoto(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    const url = new URL(value);
    if (
      !['https:', 'http:'].includes(url.protocol) ||
      !url.hostname ||
      url.username ||
      url.password
    )
      return false;
    if (
      /\.(mp4|mov|webm|avi|m4v)(?:$|\/)/i.test(url.pathname) ||
      /\/video\/upload\//i.test(url.pathname)
    )
      return false;
    // Match the backend photo contract: uploads preserve image extensions;
    // unknown extensionless hosts are not supported as required photo evidence.
    const assetName = url.pathname.split('/').pop() || '';
    return (
      /\.(jpe?g|png|webp|gif|avif|heic|heif|bmp|tiff?)$/i.test(url.pathname) ||
      (url.hostname === 'res.cloudinary.com' &&
        /\/image\/upload\//i.test(url.pathname) &&
        assetName.length > 0 &&
        !assetName.includes('.'))
    );
  } catch {
    return false;
  }
}
